import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Modal, Table } from 'react-bootstrap';
import Service from '../../../../components/Systema/Service';
import DataPagination from '../../../../components/Systema/Data/DataPagination';
import config from '../../../../config';
import { getSituacao, getUnidadeJurisdicionada } from '../emendaUtils';

const filterArquivoPasta = {
  'emendaParlamentarArquivoPasta.nome': 'ASC',
};

const formatValorBR = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '-';
  const numero = Number(valor);
  if (Number.isNaN(numero)) return valor;
  return numero.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function EmendaModal({ identificador, emenda, onHide }) {
  const [detalhe, setDetalhe] = useState(null);
  const [loading, setLoading] = useState(false);

  const serviceEmenda = useMemo(() => {
    if (!identificador) return null;
    return new Service(`${config.api_path}/public/v1/transparencia/emenda-parlamentar/${identificador}`);
  }, [identificador]);

  const serviceArquivo = useMemo(() => {
    if (!identificador || !emenda || !emenda.id) return null;
    return new Service(`${config.api_path}/public/v1/transparencia/emenda-parlamentar/arquivo/${identificador}/${emenda.id}`);
  }, [identificador, emenda]);

  useEffect(() => {
    let isMounted = true;

    if (!emenda || !emenda.id || !serviceEmenda) {
      setDetalhe(null);
      return undefined;
    }

    setLoading(true);
    setDetalhe(null);

    serviceEmenda.findById(emenda.id)
      .then((res) => {
        if (!isMounted) return;
        setDetalhe(res.data || null);
        setLoading(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setDetalhe(null);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [emenda, serviceEmenda]);

  if (!emenda) return null;

  const data = detalhe || emenda;
  const contaBancaria = formatContaBancaria(data);

  return (
    <Modal show={!!emenda} onHide={onHide} size='xl'>
      <Modal.Header closeButton>
        <Modal.Title className='w-100 d-flex justify-content-between'>
          <span>Emenda Parlamentar</span>
          <span>{data.numeroEmenda || data.id}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <Card className='mb-2'>
            <Card.Body className='text-muted'>Carregando detalhes da emenda...</Card.Body>
          </Card>
        )}

        <Card className='mb-2'>
          <Card.Body>
            <Card.Title>Informações da Emenda</Card.Title>
            <div className='emenda-modal-grid'>
              <ModalField label='Unidade Jurisdicionada' value={getUnidadeJurisdicionada(data)} />
              <ModalField label='Exercício' value={data.exercicio || '-'} />
              <ModalField label='Tipo da Emenda' value={(data.emendaParlamentarTipo && data.emendaParlamentarTipo.nome) || '-'} />
              <ModalField label='Modalidade de Execução' value={(data.emendaParlamentarModalidadeExecucao && data.emendaParlamentarModalidadeExecucao.nome) || '-'} />
              <ModalField label='Autor da Emenda' value={data.autoria || '-'} />
              <ModalField label='Número da Emenda' value={data.numeroEmenda || '-'} />
              <ModalField label='Órgão ou Entidade Concedente' value={formatPessoaJuridica(data.emendaParlamentarConcedente)} />
              <ModalField label='Órgão ou Entidade Beneficiária' value={formatPessoaJuridica(data.emendaParlamentarBeneficiario)} />
            </div>

            <div className='my-3'>
              <strong>Detalhamento do Objeto</strong>
              <p className='emenda-empenho-historico mb-0'>{data.detalhamentoObjeto || '-'}</p>
            </div>

            <div className='emenda-modal-grid'>
              <ModalField label='Número da Proposta' value={data.numeroProposta || '-'} />
              <ModalField label='Conta para Depósito' value={contaBancaria || data.contaParaDeposito || '-'} />
              <ModalField label='Ato Normativo que Aprovou a Emenda' value={data.atoNormativoAprovacao || '-'} />
              <ModalField label='Data da Aprovação da Emenda' value={formatDateBR(data.dataDaEmenda)} />
              <ModalField label='Situação da Emenda' value={getSituacao(data) || '-'} />
            </div>

            <div className='my-3'>
              <strong>Observações / Motivos de Impedimentos </strong>
              <p className='emenda-empenho-historico mb-0'>{data.observacao || '-'}</p>
            </div>

          </Card.Body>
        </Card>

        <Card className='mb-2'>
          <Card.Body>
            <Card.Title>Classificação Orçamentária</Card.Title>
            <div className='emenda-modal-grid'>
              <ModalField label='Unidade Orçamentária' value={data.classificacaoOrcamentaria ? data.classificacaoOrcamentaria.unidadeOrcamentaria : '-'} />
              <ModalField label='Função' value={data.classificacaoOrcamentaria ? data.classificacaoOrcamentaria.funcao : '-'} />
              <ModalField label='Sub-Função' value={data.classificacaoOrcamentaria ? data.classificacaoOrcamentaria.subfuncao : '-'} />
              <ModalField label='Programa' value={data.classificacaoOrcamentaria ? data.classificacaoOrcamentaria.programa : '-'} />
              <ModalField label='Ação Govenamental' value={data.classificacaoOrcamentaria ? data.classificacaoOrcamentaria.acao : '-'} />
              <ModalField label='Despesa Orçamentária' value={data.classificacaoOrcamentaria ? data.classificacaoOrcamentaria.despesa : '-'} />
              <ModalField label='Fonte de Recurso' value={data.classificacaoOrcamentaria ? data.classificacaoOrcamentaria.fonteRecurso : '-'} />
              <ModalField label='EP' value={data.emendaParlamentarTipo ? `${data.emendaParlamentarTipo.emendaParlamentarEP.codigo} - ${data.emendaParlamentarTipo.emendaParlamentarEP.nome}`  : '-'} />
            </div>
          </Card.Body>
        </Card>

        <div className='emenda-modal-totais mb-2'>
          <Card>
            <Card.Body>
              <Card.Title>Valor da Proposta</Card.Title>
              <h5 className='mb-0'>{formatValorBR(data.valorPrevisto)}</h5>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <Card.Title>Valor Arrecadado</Card.Title>
              <h5 className='mb-0'>{formatValorBR(data.valorArrecadado)}</h5>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <Card.Title>Valor Aprovado</Card.Title>
              <h5 className='mb-0'>{formatValorBR(data.valorAprovado)}</h5>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <Card.Title>Valor Empenhado</Card.Title>
              <h5 className='mb-0'>{formatValorBR(data.totalEmpenhado)}</h5>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <Card.Title>Valor Liquidado</Card.Title>
              <h5 className='mb-0'>{formatValorBR(data.totalLiquidado)}</h5>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <Card.Title>Valor Pago</Card.Title>
              <h5 className='mb-0'>{formatValorBR(data.totalPago)}</h5>
            </Card.Body>
          </Card>
        </div>

        {serviceArquivo && (
          <Card className='mb-2'>
            <Card.Body className='px-0'>
              <Card.Title className='px-4'>Arquivos Anexados</Card.Title>
              <DataPagination service={serviceArquivo} sort={filterArquivoPasta}>
                {({ data }) => (
                  <Table size='sm'>
                    <thead>
                      <tr>
                        <th>Arquivo</th>
                        <th>Seção</th>
                        <th>Download</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((row, index) => {
                        const caminho = row.caminho.split('/').slice(2).join('/');
                        const href = row.link ? row.link : `https://transparencia.systemainformatica.com.br/${caminho}`;
                        return (
                          <tr key={index}>
                            <td>{row.nome || row.caminho}</td>
                            <td>{row.emendaParlamentarArquivoPasta && row.emendaParlamentarArquivoPasta.nome}</td>
                            <td>
                              <Button size='sm' variant='outline-primary' target='_blank' href={href}>
                                Download
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                      {data.length === 0 && (
                        <tr>
                          <td colSpan={3} className='text-center text-muted'>Nenhum arquivo anexado a esta emenda</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </DataPagination>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
    </Modal>
  );
}

function ModalField({ label, value }) {
  return (
    <div className='emenda-modal-field'>
      <strong>{label}</strong>
      <span>{value || '-'}</span>
    </div>
  );
}

function formatDateBR(value) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('pt-BR');
}

function formatPessoaJuridica(item) {
  if (!item) return '-';

  const documento = item.cnpj || item.documento;
  const nome = item.nome;
  const esfera = item.esfera;

  const partes = [];
  if (documento) partes.push(documento);
  if (nome) partes.push(nome);
  if (esfera) partes.push(`(${esfera})`);

  return partes.length > 0 ? partes.join(' - ').replace(' - (', ' (') : '-';
}

function formatContaBancaria(data) {
  if (!data || !data.contaBancaria) return '';

  const { banco, agencia, conta, numero, nome } = data.contaBancaria;
  const partes = [];

  if (numero !== null && numero !== undefined && numero !== '') partes.push(numero);
  if (nome) partes.push(nome);
  if (banco) partes.push(banco);
  if (agencia) partes.push(`Ag. ${agencia}`);
  if (conta) partes.push(`Conta ${conta}`);

  return partes.join(' - ');
}
